import { z } from "zod";

/** Where a creator mainly publishes. Asked first, in the hero pill. */
export const CREATOR_PLATFORMS = ["instagram", "tiktok", "youtube", "other"] as const;
export type CreatorPlatform = (typeof CREATOR_PLATFORMS)[number];

/**
 * Audience size on the main platform, as bands rather than a number: nobody
 * knows their exact count, and a band is what the team sorts by anyway.
 */
export const CREATOR_FOLLOWER_RANGES = [
  "under_10k",
  "10k_50k",
  "50k_200k",
  "200k_1m",
  "over_1m",
] as const;
export type CreatorFollowerRange = (typeof CREATOR_FOLLOWER_RANGES)[number];

/**
 * The ways we work with creators (owner, 2026-10-06). A creator can be
 * interested in any of them, so the form takes a set, not one value.
 *
 * - free_activity: we host them at an activity, restaurant, stay or spa
 * - paid: a paid campaign, for selected creators
 *
 * "affiliate" was removed 2026-10-09: earning on bookings is now the
 * ambassador program (/ambassadors). Old leads still hold the value, so it
 * stays readable through LEGACY_CREATOR_COLLAB_TYPES for the admin.
 */
export const CREATOR_COLLAB_TYPES = ["free_activity", "paid"] as const;
export type CreatorCollabType = (typeof CREATOR_COLLAB_TYPES)[number];
export const LEGACY_CREATOR_COLLAB_TYPES = ["affiliate"] as const;

/** What the creator mostly makes. */
export const CREATOR_NICHES = [
  "travel",
  "food",
  "luxury",
  "lifestyle",
  "adventure",
  "family",
  "other",
] as const;
export type CreatorNiche = (typeof CREATOR_NICHES)[number];

/**
 * The account name out of whatever was pasted: a bare handle with or without
 * the @, or a profile URL on any of the platforms. Null when there is no
 * account name in there. Never includes the @.
 */
export function creatorHandle(raw: string): string | null {
  const match = raw
    .trim()
    .match(
      /^(?:@|(?:https?:\/\/)?(?:www\.|m\.)?(?:instagram\.com|tiktok\.com|youtube\.com)\/@?)?([A-Za-z0-9._-]{1,40})\/?$/,
    );
  return match ? match[1] : null;
}

/** One platform the creator posts on, with their account name there. */
export const creatorPlatformHandleSchema = z.object({
  platform: z.enum(CREATOR_PLATFORMS),
  handle: z
    .string()
    .trim()
    .max(200)
    .refine((v) => creatorHandle(v) !== null)
    .transform((v) => creatorHandle(v) as string),
});
export type CreatorPlatformHandle = z.output<typeof creatorPlatformHandleSchema>;

/** One to four platforms, each once, in the order the creator picked them. */
export const creatorPlatformsSchema = z
  .array(creatorPlatformHandleSchema)
  .min(1)
  .max(CREATOR_PLATFORMS.length)
  .refine((list) => new Set(list.map((p) => p.platform)).size === list.length, {
    message: "Each platform once",
  });

/**
 * Before 2026-10-09 the form sent one `platform` + `handle`. A cached page
 * still does, so that shape becomes a one-entry `platforms` list. When both
 * are sent, `platforms` wins.
 */
function liftSinglePlatform(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const r = raw as Record<string, unknown>;
  if (r.platforms !== undefined || r.platform === undefined) return raw;
  return { ...r, platforms: [{ platform: r.platform, handle: r.handle }] };
}

const creatorLeadObjectSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(4).max(40),
  country: z.string().trim().min(2).max(80),
  platforms: creatorPlatformsSchema,
  /** Overall audience across every platform. */
  followers: z.enum(CREATOR_FOLLOWER_RANGES),
  niche: z.enum(CREATOR_NICHES),
  // Legacy "affiliate" is still accepted from a cached page and dropped, so
  // a guest is not refused for it; what remains must still hold one type.
  collabTypes: z
    .array(z.enum([...CREATOR_COLLAB_TYPES, ...LEGACY_CREATOR_COLLAB_TYPES]))
    .transform((list) => CREATOR_COLLAB_TYPES.filter((c) => list.includes(c)))
    .pipe(z.array(z.enum(CREATOR_COLLAB_TYPES)).min(1)),
  pitch: z.string().trim().min(10).max(600),
  consent: z.literal(true),
  lang: z.string().min(2).max(5).default("en"),
});

/** Wire + validation contract for a creator collab application. */
export const creatorLeadSchema = z.preprocess(liftSinglePlatform, creatorLeadObjectSchema);

/** What the web form sends (the current shape; the old single pair is read, never sent). */
export type CreatorLeadInput = z.input<typeof creatorLeadObjectSchema>;
export type CreatorLeadParsed = z.output<typeof creatorLeadObjectSchema>;

/** Follow-up pipeline for creator applications (admin-managed). */
export const CREATOR_LEAD_STATUSES = ["new", "contacted", "accepted", "declined"] as const;
export type CreatorLeadStatus = (typeof CREATOR_LEAD_STATUSES)[number];

/** Wire shape of a stored creator application as consumed by the admin. */
export interface CreatorLeadDTO {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  country: string;
  /** First entry of `platforms`, kept for readers from before 2026-10-09. */
  platform: string;
  handle: string;
  /** Every platform with its account name, in pick order. */
  platforms: { platform: string; handle: string }[];
  followers: string;
  niche: string;
  collabTypes: string[];
  pitch: string;
  consent: boolean;
  lang: string;
  status: string;
  /** First time staff opened the application; null = unseen. */
  seenAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The public profile URL for a stored handle, or null for "other". */
export function creatorProfileUrl(platform: string, handle: string): string | null {
  switch (platform) {
    case "instagram":
      return `https://instagram.com/${handle}`;
    case "tiktok":
      return `https://www.tiktok.com/@${handle}`;
    case "youtube":
      return `https://www.youtube.com/@${handle}`;
    default:
      return null;
  }
}

const emptyToUndefined = (v: unknown) =>
  typeof v === "string" && v.trim() === "" ? undefined : v;

/** Query contract for the admin creator-applications list. */
export const creatorLeadsListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
  status: z.preprocess(emptyToUndefined, z.enum(CREATOR_LEAD_STATUSES).optional()),
  platform: z.preprocess(emptyToUndefined, z.enum(CREATOR_PLATFORMS).optional()),
  // Legacy values too, so the admin can still filter old affiliate leads.
  collabType: z.preprocess(
    emptyToUndefined,
    z.enum([...CREATOR_COLLAB_TYPES, ...LEGACY_CREATOR_COLLAB_TYPES]).optional(),
  ),
  q: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
});
export type CreatorLeadsListQuery = z.infer<typeof creatorLeadsListQuerySchema>;
